// Sub-tasks: a checklist under one task. Ticking every item does not close the
// task (Volkan, 2026-10-22) — the bar only shows progress.
import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Trash2 } from 'lucide-react'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import { toast } from '../../lib/toast'
import { cn } from '../../lib/utils'
import { useT } from '../../i18n'
import { staffTasksI18n } from '../../i18n/shared/staff-tasks'
import {
  addSubtask,
  deleteSubtask,
  staffTaskKeys,
  updateSubtask,
  type TaskSubtask,
} from './api'
import './StaffTasksPage.css'

type Props = { taskId: string; subtasks: TaskSubtask[]; className?: string }

export function SubtaskList({ taskId, subtasks, className }: Props) {
  const t = useT(staffTasksI18n)
  const queryClient = useQueryClient()
  const [title, setTitle] = useState('')
  const done = subtasks.filter((item) => item.is_done).length
  const total = subtasks.length

  const refresh = () => void queryClient.invalidateQueries({ queryKey: staffTaskKeys.all })
  const onError = () => toast.error(t.toasts.error)
  const add = useMutation({
    mutationFn: () => addSubtask(taskId, title.trim()),
    onSuccess: () => {
      setTitle('')
      refresh()
    },
    onError,
  })
  const toggle = useMutation({
    mutationFn: (item: TaskSubtask) => updateSubtask(taskId, item.id, { is_done: !item.is_done }),
    onSuccess: refresh,
    onError,
  })
  const remove = useMutation({
    mutationFn: (item: TaskSubtask) => deleteSubtask(taskId, item.id),
    onSuccess: refresh,
    onError,
  })

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    if (title.trim()) add.mutate()
  }

  return (
    <section className={className} aria-labelledby="staff-task-subtasks" data-testid="staff-task-subtasks">
      <div className="mb-3 flex items-center justify-between gap-3">
        <h2 id="staff-task-subtasks" className="m-0 text-sm font-semibold">
          {t.structure.subtasks}
        </h2>
        {total > 0 && (
          <span className="text-xs tabular-nums text-[var(--color-text-muted)]">
            {t.structure.subtaskProgress(done, total)}
          </span>
        )}
      </div>
      {total > 0 && (
        <div
          role="progressbar"
          aria-label={t.structure.subtasks}
          aria-valuemin={0}
          aria-valuemax={total}
          aria-valuenow={done}
          className="mb-3 h-1.5 overflow-hidden rounded-full bg-[var(--color-surface-muted)]"
        >
          <div
            className="h-full rounded-full bg-[var(--color-success)] transition-[width]"
            style={{ width: `${Math.round((done / total) * 100)}%` }}
          />
        </div>
      )}
      {total > 0 && (
        <ul className="m-0 mb-3 flex list-none flex-col gap-1 p-0">
          {subtasks.map((item) => (
            <li key={item.id} className="group flex min-h-11 items-center gap-2 rounded px-1 py-0.5 hover:bg-[var(--color-surface-muted)] sm:min-h-0">
              <input
                type="checkbox"
                id={`subtask-${item.id}`}
                checked={item.is_done}
                onChange={() => toggle.mutate(item)}
                className="h-4 w-4 shrink-0 accent-[var(--color-action)]"
              />
              <label
                htmlFor={`subtask-${item.id}`}
                className={cn(
                  'min-w-0 flex-1 py-2 text-sm sm:py-1',
                  item.is_done && 'text-[var(--color-text-muted)] line-through',
                )}
              >
                {item.title}
              </label>
              {/* 44 px on phones; always visible on touch screens (no hover there). */}
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={t.structure.removeSubtask(item.title)}
                onClick={() => remove.mutate(item)}
                data-testid={`staff-subtask-remove-${item.id}`}
                className="staff-subtask-remove shrink-0 opacity-0 hover:text-[var(--color-danger)] focus-visible:opacity-100 group-hover:opacity-100"
              >
                <Trash2 className="h-4 w-4" aria-hidden />
              </Button>
            </li>
          ))}
        </ul>
      )}
      <form onSubmit={submit} className="flex gap-2">
        <Input
          value={title}
          maxLength={200}
          placeholder={t.structure.subtaskPlaceholder}
          aria-label={t.structure.subtaskPlaceholder}
          onChange={(e) => setTitle(e.target.value)}
        />
        <Button type="submit" variant="outline" disabled={!title.trim() || add.isPending}>
          {t.structure.addSubtask}
        </Button>
      </form>
    </section>
  )
}
